import * as React from 'react';
import {useCallback, useRef} from 'react';
import {createView, AnimatePresence} from '@sail/engine';
import {useControlledState} from '@sail/react-aria';
import {useProps} from '../../hooks/useProps';
import {replaceDeprecatedProps} from './utils';
import type {PopoverProps} from './PopoverProps';
import {BottomSheet} from '../bottom-sheet/BottomSheet';
import {PopoverHeader} from './PopoverHeader';
import {ButtonConfig} from '../Button';
import {SelectConfig} from '../Select';
import {SelectFieldConfig} from '../SelectFieldConfig';
import {TextFieldConfig} from '../TextField';

export const PopoverBottomSheet = createView<PopoverProps>((allProps) => {
  const {
    targetRef,
    placement: reactAriaPlacement,
    offset: offsetOptions,
    flip: flipEnabled,
    shift: shouldShift,
    size: sizeOptions,
    inline: inlineEnabled,
    hide: hideOptions,
    onClose,
    subviews,
    dismissible,
    showOnPress,
    showOnFocus,
    showOnHover,
    role,
    defaultOpen,
    open: controlledOpen,
    mobileTitle,
    title,
    onOpenChange: setControlledOpen,
    floatingStrategy,
    animate: shouldAnimate = true,
    useReactAriaDismissOutside,
    children,
    // We don't want to spread inherits to the Popover mobile, as people
    // will be thinking about the desktop version of the Popover when they
    // pass them. If we ever want to allow bottom sheet customization,
    // we can then expose a subview.
    inherits,
    ...props
  } = replaceDeprecatedProps(allProps);

  const [open, setOpen] = useControlledState(
    controlledOpen ?? (defaultOpen === undefined ? true : undefined),
    defaultOpen,
    setControlledOpen,
  );

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        onClose?.();
      }
    },
    [setOpen, onClose],
  );

  const isUncontrolled = typeof controlledOpen === 'undefined';

  useProps(targetRef, {
    onClick: () => {
      if (isUncontrolled) {
        onOpenChange(!open);
      }
    },
  });

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const layer = (
    <BottomSheet
      {...props}
      contentWrapperRef={contentWrapperRef}
      requestClose={() => onOpenChange?.(false)}
      closeConfirmationDialog={null}
      focusScopeProps={{
        autoFocus: true,
        restoreFocus: true,
        contain: false,
      }}
      layerName="modal"
      mobileDisplay="bottom-sheet"
      shouldShowBackdrop
      shouldAnimate={shouldAnimate}
      inherits={subviews.mobile}
      subviews={{layer: subviews.layer}}
      // Custom props for the content wrapper
      css={{stack: 'y'}}
      data-testid={props['data-testid'] ?? 'popover-layer'}
      uses={[
        ButtonConfig.customize({defaults: {size: 'large'}}),
        SelectConfig.customize({defaults: {size: 'large'}}),
        SelectFieldConfig.customize({defaults: {size: 'large'}}),
        TextFieldConfig.customize({defaults: {size: 'large'}}),
      ]}
    >
      <PopoverHeader
        onClose={() => onOpenChange?.(false)}
        title={mobileTitle || title}
      />
      {children}
    </BottomSheet>
  );

  return <AnimatePresence>{open ? layer : null}</AnimatePresence>;
});
